import { FC, useEffect, useMemo, useState } from 'react'
import { Grid } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from 'dataStore/state'
import getUniqueCitiesFromPizzerias from 'handlers/getUniqueCitiesFromPizzerias'
import { useRole } from 'hooks/useRole'
import {
  DEFAULT_CUSTOMERS_FILTER,
  ICustomersFilterState,
} from './customersFilter.types'
import TextFilterField from './TextFilterField'
import CityFilter from './CityFilter'
import CustomersPizzeriaFilter from './CustomersPizzeriaFilter'

interface CustomersFiltersProps {
  /** Применённые фильтры (после debounce для текста) */
  filters: ICustomersFilterState
  /**
   * Колбэк применения фильтров.
   * @param nextFilters — новое состояние фильтров
   */
  onFiltersChange: (nextFilters: ICustomersFilterState) => void
}

/**
 * Блок фильтров страницы клиентов.
 * @param filters — текущие применённые фильтры
 * @param onFiltersChange — колбэк применения
 */
const CustomersFilters: FC<CustomersFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const { pizzerias } = useSelector((state: RootState) => state.pizzerias)
  const { isManager } = useRole()
  const [draftFilters, setDraftFilters] = useState<ICustomersFilterState>(
    filters ?? DEFAULT_CUSTOMERS_FILTER
  )

  const cities = useMemo(
    () => getUniqueCitiesFromPizzerias(pizzerias),
    [pizzerias]
  )

  useEffect(() => {
    setDraftFilters(filters)
  }, [filters])

  // Debounce текстовых полей ~400ms
  useEffect(() => {
    const debounceTimerId = window.setTimeout(() => {
      const textUnchanged =
        draftFilters.first_name === filters.first_name &&
        draftFilters.last_name === filters.last_name &&
        draftFilters.phone === filters.phone &&
        draftFilters.email === filters.email

      if (textUnchanged) {
        return
      }

      onFiltersChange({
        ...filters,
        first_name: draftFilters.first_name,
        last_name: draftFilters.last_name,
        phone: draftFilters.phone,
        email: draftFilters.email,
      })
    }, 400)

    return () => window.clearTimeout(debounceTimerId)
  }, [draftFilters, filters, onFiltersChange])

  /**
   * Обновляет одно текстовое поле в draft.
   * @param fieldName — имя поля
   * @param value — новое значение
   */
  const handleTextChange = (
    fieldName: keyof Pick<
      ICustomersFilterState,
      'first_name' | 'last_name' | 'phone' | 'email'
    >,
    value: string
  ): void => {
    setDraftFilters(previousFilters => ({
      ...previousFilters,
      [fieldName]: value,
    }))
  }

  return (
    <Grid container spacing={3} sx={{ mb: '25px' }}>
      <Grid item xs={12} sm={6} md={3}>
        <TextFilterField
          label='Имя'
          value={draftFilters.first_name}
          onChange={value => handleTextChange('first_name', value)}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextFilterField
          label='Фамилия'
          value={draftFilters.last_name}
          onChange={value => handleTextChange('last_name', value)}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextFilterField
          label='Телефон'
          value={draftFilters.phone}
          onChange={value => handleTextChange('phone', value)}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextFilterField
          label='Email'
          value={draftFilters.email}
          onChange={value => handleTextChange('email', value)}
        />
      </Grid>
      {!isManager && (
        <Grid item xs={12} sm={6} md={3}>
          <CityFilter
            cities={cities}
            selectedCity={filters.city}
            onChange={city => onFiltersChange({ ...filters, city })}
          />
        </Grid>
      )}
      {!isManager && (
        <Grid item xs={12} sm={6} md={3}>
          <CustomersPizzeriaFilter
            selectedPizzeriaId={filters.pizzeria_id}
            onChange={pizzeriaId =>
              onFiltersChange({ ...filters, pizzeria_id: pizzeriaId })
            }
          />
        </Grid>
      )}
    </Grid>
  )
}

export default CustomersFilters
